/**
 * Trip timeline planning for Executive Calendar event groups.
 */

import { addMinutes, parseISO } from 'date-fns';
import { estimateDrivingSecondsFromCoords } from './drivingDurationEstimate';
import { toGoogleCalendarDateTime } from './googleCalendarDateTime';
import type {
  CalendarEventGroup,
  CalendarEventGroupAnchorMode,
  CalendarEventGroupSegment,
  CalendarEventSegmentType,
  SavedCalendarPlace,
} from '../types';
import type { EventGroupSegmentPayload } from './executiveCalendarDelegatedService';

export interface CalendarActivityStop {
  id: string;
  placeId: string;
  title: string;
  durationMinutes: number;
  calendarId: string;
}

export interface PlannedSegment {
  segmentId: string;
  type: CalendarEventSegmentType;
  title: string;
  location?: string;
  fromPlaceId?: string;
  toPlaceId?: string;
  fromAddress?: string;
  toAddress?: string;
  fromLat?: number;
  fromLng?: number;
  toLat?: number;
  toLng?: number;
  calendarId: string;
  durationMinutes: number;
  /** Raw drive minutes before travel buffer (travel legs only). */
  driveDurationMinutes?: number;
  travelDurationSeconds?: number;
  start?: Date;
  end?: Date;
}

export type DrivingDurationInput = {
  origin: string;
  destination: string;
  originLat?: number;
  originLng?: number;
  destinationLat?: number;
  destinationLng?: number;
};

export type GetDrivingDurationFn = (
  input: DrivingDurationInput,
) => Promise<{ durationSeconds: number; distanceMeters?: number; estimated?: boolean }>;

export type TravelDurationComputeResult = {
  segments: PlannedSegment[];
  warnings: string[];
};

function newSegmentId(): string {
  return `seg_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function travelTitle(fromLabel: string, toLabel: string): string {
  return `Drive: ${fromLabel} → ${toLabel}`;
}

function placeLabel(place: SavedCalendarPlace | undefined, fallback: string): string {
  return place?.label?.trim() || fallback;
}

/** Expand start/end places + activity stops into alternating travel/activity segments. */
export function buildTripSegments(
  startPlace: SavedCalendarPlace,
  endPlace: SavedCalendarPlace,
  activities: CalendarActivityStop[],
  placesById: Map<string, SavedCalendarPlace>,
  travelCalendarId: string,
): PlannedSegment[] {
  if (activities.length === 0) return [];

  const segments: PlannedSegment[] = [];
  let currentPlace = startPlace;

  for (const activity of activities) {
    const dest = placesById.get(activity.placeId);
    if (!dest) continue;

    segments.push({
      segmentId: newSegmentId(),
      type: 'travel',
      title: travelTitle(placeLabel(currentPlace, 'Start'), placeLabel(dest, 'Stop')),
      fromPlaceId: currentPlace.id,
      toPlaceId: dest.id,
      fromAddress: currentPlace.address,
      toAddress: dest.address,
      fromLat: currentPlace.lat,
      fromLng: currentPlace.lng,
      toLat: dest.lat,
      toLng: dest.lng,
      calendarId: travelCalendarId,
      durationMinutes: 15,
    });

    segments.push({
      segmentId: newSegmentId(),
      type: 'activity',
      title: activity.title,
      location: dest.address,
      toPlaceId: dest.id,
      calendarId: activity.calendarId,
      durationMinutes: activity.durationMinutes,
    });

    currentPlace = dest;
  }

  segments.push({
    segmentId: newSegmentId(),
    type: 'travel',
    title: travelTitle(placeLabel(currentPlace, 'Stop'), placeLabel(endPlace, 'End')),
    fromPlaceId: currentPlace.id,
    toPlaceId: endPlace.id,
    fromAddress: currentPlace.address,
    toAddress: endPlace.address,
    fromLat: currentPlace.lat,
    fromLng: currentPlace.lng,
    toLat: endPlace.lat,
    toLng: endPlace.lng,
    calendarId: travelCalendarId,
    durationMinutes: 15,
  });

  return segments;
}

/** Gym quick template: Home → Gym → Home with one activity. */
export function buildGymTripSegments(
  home: SavedCalendarPlace,
  gym: SavedCalendarPlace,
  activityTitle: string,
  durationMinutes: number,
  activityCalendarId: string,
  travelCalendarId: string,
): PlannedSegment[] {
  return buildTripSegments(
    home,
    home,
    [
      {
        id: 'gym-activity',
        placeId: gym.id,
        title: activityTitle,
        durationMinutes,
        calendarId: activityCalendarId,
      },
    ],
    new Map([
      [home.id, home],
      [gym.id, gym],
    ]),
    travelCalendarId,
  );
}

export function secondsToMinutes(seconds: number): number {
  return Math.max(1, Math.ceil(seconds / 60));
}

/** Extra padding added to raw drive time before scheduling travel blocks. */
export function travelBufferMinutes(driveMinutes: number): number {
  if (driveMinutes < 20) return 5;
  if (driveMinutes < 60) return 10;
  return 20;
}

export function travelDurationWithBuffer(driveMinutes: number): number {
  return driveMinutes + travelBufferMinutes(driveMinutes);
}

function applyDriveAndBuffer(seg: PlannedSegment, durationSeconds: number): void {
  seg.travelDurationSeconds = durationSeconds;
  const driveMinutes = secondsToMinutes(durationSeconds);
  seg.driveDurationMinutes = driveMinutes;
  seg.durationMinutes = travelDurationWithBuffer(driveMinutes);
}

/** Preview label for segment duration, e.g. travel "(28 + 5)" or activity "60 min". */
export function formatSegmentDurationLabel(seg: PlannedSegment): string {
  if (seg.type === 'travel') {
    const drive =
      seg.driveDurationMinutes ??
      (seg.travelDurationSeconds != null
        ? secondsToMinutes(seg.travelDurationSeconds)
        : undefined);
    if (drive != null) {
      return `${drive} + ${travelBufferMinutes(drive)}`;
    }
  }
  return `${seg.durationMinutes} min`;
}

/** Fill travel leg durations via Google Distance Matrix (with coordinate fallback estimate). */
export async function computeTravelDurations(
  segments: PlannedSegment[],
  getDrivingDuration: GetDrivingDurationFn,
): Promise<TravelDurationComputeResult> {
  const next = segments.map(s => ({ ...s }));
  const warnings: string[] = [];

  for (const seg of next) {
    if (seg.type !== 'travel' || !seg.fromAddress || !seg.toAddress) continue;

    try {
      const result = await getDrivingDuration({
        origin: seg.fromAddress,
        destination: seg.toAddress,
        originLat: seg.fromLat,
        originLng: seg.fromLng,
        destinationLat: seg.toLat,
        destinationLng: seg.toLng,
      });
      applyDriveAndBuffer(seg, result.durationSeconds);
      const driveMinutes = secondsToMinutes(result.durationSeconds);
      if (result.estimated) {
        warnings.push(`${seg.title}: estimated ${driveMinutes} min drive + ${travelBufferMinutes(driveMinutes)} min buffer`);
      }
    } catch (err) {
      if (
        seg.fromLat != null &&
        seg.fromLng != null &&
        seg.toLat != null &&
        seg.toLng != null
      ) {
        const durationSeconds = estimateDrivingSecondsFromCoords(
          seg.fromLat,
          seg.fromLng,
          seg.toLat,
          seg.toLng,
        );
        applyDriveAndBuffer(seg, durationSeconds);
        const driveMinutes = secondsToMinutes(durationSeconds);
        warnings.push(
          `${seg.title}: estimated ${driveMinutes} min drive + ${travelBufferMinutes(driveMinutes)} min buffer (${err instanceof Error ? err.message : 'routing API failed'})`,
        );
      } else {
        const driveMinutes = seg.durationMinutes;
        seg.driveDurationMinutes = driveMinutes;
        seg.durationMinutes = travelDurationWithBuffer(driveMinutes);
        warnings.push(
          `${seg.title}: kept default ${driveMinutes} min drive + ${travelBufferMinutes(driveMinutes)} min buffer — ${err instanceof Error ? err.message : 'could not compute drive time'}`,
        );
      }
    }
  }

  return { segments: next, warnings };
}

/** Index of the Nth activity segment (0-based activity index). */
export function activitySegmentIndex(segments: PlannedSegment[], activityIndex: number): number {
  let count = -1;
  for (let i = 0; i < segments.length; i++) {
    if (segments[i].type === 'activity') {
      count++;
      if (count === activityIndex) return i;
    }
  }
  return -1;
}

export function sumDurationMinutesBefore(segments: PlannedSegment[], beforeIndex: number): number {
  let total = 0;
  for (let i = 0; i < beforeIndex; i++) {
    total += segments[i].durationMinutes;
  }
  return total;
}

/** Assign start/end Date to each segment from anchor mode + anchor time. */
export function scheduleSegments(
  segments: PlannedSegment[],
  anchorMode: CalendarEventGroupAnchorMode,
  anchorTime: Date,
  anchorSegmentIndex: number,
): PlannedSegment[] {
  if (segments.length === 0) return [];

  const next = segments.map(s => ({ ...s }));
  let tripStart: Date;

  if (anchorMode === 'leave_home') {
    tripStart = anchorTime;
  } else {
    const minutesBefore = sumDurationMinutesBefore(next, anchorSegmentIndex);
    tripStart = addMinutes(anchorTime, -minutesBefore);
  }

  let cursor = tripStart;
  for (const seg of next) {
    seg.start = cursor;
    seg.end = addMinutes(cursor, seg.durationMinutes);
    cursor = seg.end;
  }

  return next;
}

/** Shift all segment times by deltaMs (for group drag). */
export function shiftScheduledSegments(segments: PlannedSegment[], deltaMs: number): PlannedSegment[] {
  return segments.map(seg => {
    if (!seg.start || !seg.end) return { ...seg };
    return {
      ...seg,
      start: new Date(seg.start.getTime() + deltaMs),
      end: new Date(seg.end.getTime() + deltaMs),
    };
  });
}

/** Re-schedule after primary activity start or duration change. */
export function rescheduleFromActivityChange(
  segments: PlannedSegment[],
  anchorMode: CalendarEventGroupAnchorMode,
  anchorSegmentIndex: number,
  newActivityStart: Date,
  newActivityDurationMinutes: number,
): PlannedSegment[] {
  const next = segments.map(s => ({ ...s }));
  const anchor = next[anchorSegmentIndex];
  if (!anchor || anchor.type !== 'activity') return scheduleSegments(next, anchorMode, newActivityStart, anchorSegmentIndex);

  anchor.durationMinutes = newActivityDurationMinutes;

  if (anchorMode === 'activity_start') {
    return scheduleSegments(next, 'activity_start', newActivityStart, anchorSegmentIndex);
  }

  const minutesBefore = sumDurationMinutesBefore(next, anchorSegmentIndex);
  const tripStart = addMinutes(newActivityStart, -minutesBefore);
  return scheduleSegments(next, 'leave_home', tripStart, anchorSegmentIndex);
}

export function plannedToGroupSegments(segments: PlannedSegment[]): CalendarEventGroupSegment[] {
  return segments.map(seg => ({
    segmentId: seg.segmentId,
    type: seg.type,
    calendarId: seg.calendarId,
    title: seg.title,
    location: seg.location,
    fromPlaceId: seg.fromPlaceId,
    toPlaceId: seg.toPlaceId,
    durationMinutes: seg.durationMinutes,
    travelDurationSeconds: seg.travelDurationSeconds,
    start: seg.start ? toGoogleCalendarDateTime(seg.start) : undefined,
    end: seg.end ? toGoogleCalendarDateTime(seg.end) : undefined,
  }));
}

export function groupSegmentsToPlanned(group: CalendarEventGroup): PlannedSegment[] {
  return group.segments.map(s => ({
    segmentId: s.segmentId,
    type: s.type,
    title: s.title,
    location: s.location,
    fromPlaceId: s.fromPlaceId,
    toPlaceId: s.toPlaceId,
    calendarId: s.calendarId,
    durationMinutes: s.durationMinutes,
    travelDurationSeconds: s.travelDurationSeconds,
    start: s.start ? parseISO(s.start) : undefined,
    end: s.end ? parseISO(s.end) : undefined,
  }));
}

export function plannedToSyncPayload(segments: PlannedSegment[]): EventGroupSegmentPayload[] {
  return segments.map(seg => ({
    segmentId: seg.segmentId,
    type: seg.type,
    calendarId: seg.calendarId,
    title: seg.title,
    location: seg.location,
    fromPlaceId: seg.fromPlaceId,
    toPlaceId: seg.toPlaceId,
    durationMinutes: seg.durationMinutes,
    travelDurationSeconds: seg.travelDurationSeconds,
    start: toGoogleCalendarDateTime(seg.start!),
    end: toGoogleCalendarDateTime(seg.end!),
  }));
}
